/**
 * The last subscription status the server gave THIS account, kept on the device
 * (the `storage` service — AsyncStorage on native), for the one moment the app has
 * nothing better: a first load that fails (a cold open with the network down).
 *
 * Before (review of #3988/#4031): that failure reset the hook to trial / pre_trial,
 * so an offline expired user or a lapsed payer read "Start your free 7-day trial".
 * Now a failed first load restores this snapshot when there is one for the signed-in
 * account; with none, the status stays UNAVAILABLE (a neutral state with a retry) —
 * never a trial pitch.
 *
 * Keyed and checked by account: a snapshot is only ever restored for the account it
 * was saved for (two accounts on one phone never see each other's status). Pure
 * helpers here; the hook does the reading and writing.
 */

export const SNAPSHOT_KEY_PREFIX = 'subscription_status_snapshot:';
export const SNAPSHOT_VERSION = 1;
/** Older than this, a snapshot says too little about today to be shown. */
export const SNAPSHOT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Each retry waits its step of FIRST_LOAD_RETRY_DELAYS_MS, give or take this share. */
export const RETRY_JITTER = 0.2;

/** Retries of a check still owed a fresh answer (nothing shown, or a saved status
 *  shown), in ms: then every last step until one succeeds. */
export const FIRST_LOAD_RETRY_DELAYS_MS: readonly number[] = [10_000, 30_000, 60_000, 120_000];

export interface SubscriptionSnapshot {
  v: number;
  accountId: string;
  savedAt: number;
  /** The subscription-status response body, as the server sent it. */
  data: Record<string, unknown>;
}

export function snapshotKey(accountId: string): string {
  return `${SNAPSHOT_KEY_PREFIX}${accountId}`;
}

export function encodeSnapshot(accountId: string, data: Record<string, unknown>, now: number = Date.now()): string {
  const snap: SubscriptionSnapshot = { v: SNAPSHOT_VERSION, accountId, savedAt: now, data };
  return JSON.stringify(snap);
}

/** The saved status for ``accountId``, or null (none, another account's, too old, or unreadable). */
export function decodeSnapshot(
  raw: string | null | undefined,
  accountId: string | null | undefined,
  now: number = Date.now(),
): Record<string, unknown> | null {
  if (!raw || !accountId) return null;
  try {
    const snap = JSON.parse(raw) as Partial<SubscriptionSnapshot>;
    if (!snap || snap.v !== SNAPSHOT_VERSION || snap.accountId !== accountId) return null;
    if (typeof snap.savedAt !== 'number' || now - snap.savedAt > SNAPSHOT_MAX_AGE_MS || snap.savedAt > now + 60_000) {
      return null;
    }
    if (!snap.data || typeof snap.data !== 'object' || typeof (snap.data as { tier?: unknown }).tier !== 'string') {
      return null;
    }
    if (snapshotHasEnded(snap.data as Record<string, unknown>, now)) return null;
    return snap.data as Record<string, unknown>;
  } catch {
    return null;
  }
}

function endedBy(value: unknown, now: number): boolean {
  if (typeof value !== 'string' || !value) return false;
  const t = Date.parse(value);
  return Number.isFinite(t) && t <= now;
}

/**
 * True when the saved status has already ended by its own dates — a trial whose
 * clock ran out, or a paid period (active or cancelled-in-grace) whose end date has
 * passed. It would describe access the account no longer has, so it is not restored
 * (the status is UNAVAILABLE instead). Within those dates the 30-day limit applies.
 */
export function snapshotHasEnded(data: Record<string, unknown>, now: number = Date.now()): boolean {
  const cohort = data.cohort;
  const isTrial = cohort === 'trial_active' || (cohort == null && data.status === 'trial');
  if (isTrial && endedBy(data.trial_expires_at, now)) return true;
  const isPaid = cohort === 'paid_active' || cohort === 'paid_cancelled_grace'
    || (cohort == null && data.status === 'active');
  if (isPaid && endedBy(data.subscription_ends_at, now)) return true;
  return false;
}

/**
 * What a FAILED status check leaves on screen:
 *   - ``keep``: a status is already shown (first load done) — it stays;
 *   - ``restore``: nothing shown yet, but this account has a saved status — show it;
 *   - ``unavailable``: nothing to show — the neutral loading/error state with a retry.
 * Never "reset to a new trial".
 */
export function failedCheckOutcome(
  statusShown: boolean,
  snapshot: Record<string, unknown> | null,
): 'keep' | 'restore' | 'unavailable' {
  if (statusShown) return 'keep';
  return snapshot ? 'restore' : 'unavailable';
}

/**
 * The wait before retry ``attempt`` (0-based): its step, ±20% at random so the
 * phones that lost the network together do not all come back in the same second.
 * ``random`` is injectable for tests (a value in [0, 1); 0.5 = the step exactly).
 */
export function retryDelayMs(attempt: number, random: () => number = Math.random): number {
  const i = Math.min(Math.max(attempt, 0), FIRST_LOAD_RETRY_DELAYS_MS.length - 1);
  const base = FIRST_LOAD_RETRY_DELAYS_MS[i];
  const r = Math.min(Math.max(random(), 0), 1);
  return Math.round(base * (1 - RETRY_JITTER + 2 * RETRY_JITTER * r));
}
